import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { ErrorModal, SuccessModal } from "@components";
import {
  TicketDetailsBody,
  TicketDetailsComment,
  TicketDetailsHeader,
} from "./components";

import { useTicket } from "@hooks/tickets/e-tickets/useTicket";
import { useTicketDropdown } from "@hooks/tickets/ticket-settings/useTicketDropdown";
import { useUser } from "@hooks/useUser";

import { STATUS } from "@constants/status.hrm";
import { URLS } from "@constants/urls";

import { updateTicket } from "@api/e-tickets/ticket";

import {
  TicketFormDataType,
  ticketFormValidation,
} from "@validations/Tickets/ticketFormValidation";
import { getAccess } from "@helpers/permissionController";

const { SUCCESS, FAILED } = STATUS;
const { TICKET_CATEGORIES, TICKET_TYPES, TICKET_PRIORITIES, TICKET_STATUS } =
  URLS;

const TicketDetails = () => {
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [isAssignDisabled, setIsAssignDisabled] = useState<boolean>(true);

  const { id } = useParams();
  const { pathname } = useLocation();
  const { user } = useUser();
  const { realm_roles: roles } = user || {};

  const ticketDetailsData = useTicket(id);
  const ticketTypes = useTicketDropdown(TICKET_TYPES);
  const categories = useTicketDropdown(TICKET_CATEGORIES);
  const priorities = useTicketDropdown(TICKET_PRIORITIES);
  const statuses = useTicketDropdown(TICKET_STATUS);
  const ticketDropdown = { ticketTypes, statuses, categories, priorities };
  const assignedTo = ticketDetailsData?.data?.assignedTo?.id;

  const navigate = useNavigate();

  // error and success modal
  const closeErrorModal = () => setIsErrorModalOpen(false);
  const redirectToListPage = () => navigate(pathname.replace(/\/[^/]+$/, ""));

  useEffect(() => {
    const reset = () => {
      setIsErrorModalOpen(false);
      setIsSuccessModalOpen(false);
    };
    return reset();
  }, []);

  const methods = useForm<TicketFormDataType>({
    resolver: yupResolver(ticketFormValidation),
    values: {
      ...ticketDetailsData?.data,
      assignedTo: assignedTo,
      unitId: ticketDetailsData?.data?.assignedTo?.unitId,
    },
  });

  const onSubmit: SubmitHandler<TicketFormDataType> = (data: any) => {
    const { unitId, ...rest } = data;
    updateTicket(
      rest.id,
      {
        assignedId: rest.assignedTo,
        status: rest.status,
      },
      (response) => {
        closeErrorModal();
        if (response.status === SUCCESS) setIsSuccessModalOpen(true);
        else if (response.status === FAILED) setIsErrorModalOpen(true);
      },
    );
  };

  useEffect(() => {
    setIsAssignDisabled(getAccess(roles, "E_TICKET_ASSIGN"));
  }, [roles]);

  return (
    <div className="p-10">
      {/* MODAL SECTION */}
      <>
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={redirectToListPage}
          subtitle="Ticket Updated Successfully"
        />
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
      </>
      {/* MODAL SECTION */}
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <TicketDetailsHeader
            id={id}
            redirectToListPage={redirectToListPage}
          />
          <TicketDetailsBody
            data={ticketDetailsData?.data}
            ticketDropdown={ticketDropdown}
            redirectToListPage={redirectToListPage}
            isAssignDisabled={!isAssignDisabled}
          />
        </form>
      </FormProvider>
      {/* comments section */}
      <TicketDetailsComment
        data={ticketDetailsData}
        ticketDropdown={ticketDropdown}
      />
    </div>
  );
};

export default TicketDetails;
